import { useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingBag, Ticket, X, Minus, Plus, ArrowUpRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useMerchCart, maxPurchasable } from '../context/MerchCartContext';

const API = process.env.REACT_APP_BACKEND_URL;
const DISPLAY = { fontFamily: "'Unbounded', sans-serif" };

const isSoldOut = (p) => (p.variants || []).length > 0
  ? p.variants.every(v => v.stock !== 0 && v.stock <= 0)
  : (p.stock !== 0 && p.stock <= 0);

// Remaining units (null = unlimited/agotado). stock semantics: 0 = unlimited, negative = sold out
const LOW_STOCK = 10;
const remainingStock = (p) => {
  if ((p.variants || []).length > 0) {
    const stocks = (p.variants || []).map(v => Number(v.stock || 0));
    if (stocks.some(s => s === 0)) return null;
    const total = stocks.filter(s => s > 0).reduce((a, b) => a + b, 0);
    return total > 0 ? total : null;
  }
  const s = Number(p.stock || 0);
  return s > 0 ? s : null;
};
const lowStockLabel = (p) => {
  const r = remainingStock(p);
  if (r === null || r > LOW_STOCK) return null;
  return r === 1 ? '¡Última unidad!' : `Quedan ${r} unidades`;
};

/* ---------- Product detail modal (mobile tap / "ver detalle") ---------- */
const ProductModal = ({ product, onClose }) => {
  const { addItem } = useMerchCart();
  const [variant, setVariant] = useState(null);
  const [qty, setQty] = useState(1);
  const hasVariants = (product.variants || []).length > 0;

  const add = () => {
    if (hasVariants && !variant) return toast.error(`Elegí ${product.variant_label || 'una opción'}`);
    const maxQty = maxPurchasable(product, variant || null);
    if (maxQty <= 0) return toast.error('Sin stock disponible');
    if (qty > maxQty) { setQty(maxQty); return toast.error(`Solo quedan ${maxQty} unidades — ajustamos la cantidad`); }
    addItem(product, variant, qty);
    toast.success('Agregado al carrito');
    onClose();
  };

  const [activeImg, setActiveImg] = useState(0);
  const gallery = product.images || [];

  return (
    <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center sm:p-4" data-testid="merch-product-modal">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} className="relative w-full max-w-lg bg-[#0B0B0B] border-t sm:border border-white/10 overflow-hidden max-h-[92vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-3 right-3 z-10 w-9 h-9 bg-black/70 text-white flex items-center justify-center" data-testid="merch-modal-close"><X size={16} /></button>
        {gallery[activeImg] && <img src={gallery[activeImg]} alt={product.name} className="w-full h-72 object-cover" data-testid="merch-modal-main-image" />}
        {gallery.length > 1 && (
          <div className="flex gap-2 px-6 pt-4 overflow-x-auto" data-testid="merch-modal-gallery">
            {gallery.map((img, idx) => (
              <button key={img} onClick={() => setActiveImg(idx)} className={`flex-shrink-0 w-14 h-14 border ${idx === activeImg ? 'border-[#D4AF37]' : 'border-white/10 opacity-60'}`} data-testid={`merch-modal-thumb-${idx}`}>
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
        <div className="p-6 space-y-5">
          {product.event && (
            <Link to={`/evento/${product.event.id}`} className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-white bg-white/10 backdrop-blur-md border border-white/20 px-3 py-1.5">
              <Ticket size={10} className="text-[#D4AF37]" /> Exclusivo · {product.event.title}
            </Link>
          )}
          <div className="flex items-start justify-between gap-4">
            <h2 className="text-white text-xl font-bold uppercase leading-tight" style={DISPLAY}>{product.name}</h2>
            <p className="text-[#D4AF37] text-xl font-black whitespace-nowrap" style={DISPLAY}>${Number(product.price).toLocaleString('es-AR')}</p>
          </div>
          {product.description && <p className="text-white/45 text-sm leading-relaxed">{product.description}</p>}
          {lowStockLabel(product) && (
            <p className="inline-flex items-center gap-1.5 text-[#D4AF37] text-[11px] font-black uppercase tracking-[0.2em]" data-testid="merch-modal-lowstock">
              🔥 {lowStockLabel(product)}
            </p>
          )}
          {hasVariants && (
            <div>
              <p className="text-white/40 text-[10px] uppercase tracking-[0.25em] font-bold mb-2">{product.variant_label || 'Opción'}</p>
              <div className="flex flex-wrap gap-2">
                {product.variants.map(v => {
                  const out = v.stock !== 0 && v.stock <= 0;
                  const vLow = !out && v.stock > 0 && v.stock <= 5;
                  return (
                    <button key={v.label} disabled={out} onClick={() => { setVariant(v.label); const m = maxPurchasable(product, v.label); if (m > 0) setQty(q => Math.min(q, m)); }} aria-label={`${product.variant_label || 'Opción'} ${v.label}`}
                      title={vLow ? `Quedan ${v.stock}` : undefined}
                      className={`min-w-[44px] h-11 px-3 text-xs font-bold border transition-colors ${variant === v.label ? 'border-[#D4AF37] text-[#D4AF37]' : out ? 'border-white/10 text-white/20 line-through cursor-not-allowed' : 'border-white/25 text-white hover:border-white'}`}
                      data-testid={`variant-option-${v.label}`}>
                      {v.label}{vLow ? <span className="text-[9px] font-normal opacity-70"> · {v.stock}</span> : null}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="flex items-center gap-3">
            <div className="flex items-center border border-white/25">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-11 text-white flex items-center justify-center" data-testid="qty-minus"><Minus size={13} /></button>
              <span className="text-white font-bold w-8 text-center text-sm" data-testid="qty-value">{qty}</span>
              <button onClick={() => {
                const maxQty = hasVariants && !variant ? 20 : maxPurchasable(product, variant || null);
                if (qty >= maxQty) { toast.error(maxQty <= 1 ? 'No hay más unidades disponibles' : `Solo quedan ${maxQty} unidades disponibles`); return; }
                setQty(Math.min(20, qty + 1));
              }} className="w-10 h-11 text-white flex items-center justify-center" data-testid="qty-plus"><Plus size={13} /></button>
            </div>
            <button onClick={add} className="flex-1 h-11 bg-[#D4AF37] text-black text-xs font-black uppercase tracking-[0.2em] hover:bg-[#F2D272] transition-colors" data-testid="add-to-cart-btn">
              Agregar · ${(product.price * qty).toLocaleString('es-AR')}
            </button>
          </div>
          <p className="text-white/30 text-[11px] uppercase tracking-wider">
            {product.delivery_options === 'pickup' ? 'Retiro en el evento' : product.delivery_options === 'shipping' ? 'Envío a domicilio' : 'Retiro en el evento o envío a domicilio'}
          </p>
        </div>
      </motion.div>
    </div>
  );
};

/* ---------- Editorial bento tile ---------- */
const ProductTile = ({ product, index, span, onOpen }) => {
  const { addItem } = useMerchCart();
  const [variant, setVariant] = useState(null);
  const hasVariants = (product.variants || []).length > 0;
  const soldOut = isSoldOut(product);
  const featured = span.featured;

  const quickAdd = (e) => {
    e.stopPropagation();
    if (hasVariants && !variant) return toast.error(`Elegí ${product.variant_label || 'una opción'}`);
    addItem(product, variant, 1);
    toast.success('Agregado al carrito');
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.55, delay: (index % 4) * 0.08, ease: 'easeOut' }}
      className={`relative group cursor-pointer overflow-hidden bg-[#0D0D0D] ${span.cls}`}
      onClick={() => !soldOut && onOpen(product)}
      data-testid={`merch-product-${product.id}`}
    >
      {/* image */}
      <div className="absolute inset-0">
        {(product.images || [])[0] ? (
          <>
            <img src={product.images[0]} alt={product.name}
              className={`w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 ${soldOut ? 'grayscale opacity-40' : ''} ${(product.images || [])[1] ? 'group-hover:opacity-0 transition-opacity' : ''}`} />
            {(product.images || [])[1] && (
              <img src={product.images[1]} alt={product.name}
                className={`absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${soldOut ? 'grayscale' : ''}`} />
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-[#101010]"><ShoppingBag size={featured ? 64 : 36} className="text-white/10" /></div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent" />
      </div>

      {/* index number */}
      <span className={`absolute top-4 left-5 text-white/25 font-black ${featured ? 'text-2xl' : 'text-base'}`} style={DISPLAY}>
        {String(index + 1).padStart(2, '0')}
      </span>

      {/* event badge */}
      {product.event && (
        <span className="absolute top-4 right-4 inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.2em] text-white bg-white/10 backdrop-blur-md border border-white/20 px-2.5 py-1.5" data-testid={`merch-event-badge-${product.id}`}>
          <Ticket size={9} className="text-[#D4AF37]" /> {product.event.title}
        </span>
      )}

      {/* sold out stamp */}
      {soldOut && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="border-4 border-white/80 text-white/90 px-6 py-2 text-xl md:text-2xl font-black uppercase tracking-[0.3em] -rotate-6" style={DISPLAY} data-testid={`merch-soldout-${product.id}`}>Agotado</span>
        </div>
      )}

      {/* low stock badge */}
      {!soldOut && lowStockLabel(product) && (
        <span className={`absolute right-4 ${product.event ? 'top-12' : 'top-4'} inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-[0.2em] text-black bg-[#D4AF37] px-2.5 py-1.5 animate-pulse`} data-testid={`merch-lowstock-${product.id}`}>
          🔥 {lowStockLabel(product)}
        </span>
      )}

      {/* bottom info — always visible */}
      <div className="absolute inset-x-0 bottom-0 p-5 flex items-end justify-between gap-3">
        <div className="min-w-0">
          <h3 className={`text-white font-bold uppercase leading-tight ${featured ? 'text-2xl md:text-3xl' : 'text-sm md:text-base'}`} style={DISPLAY}>{product.name}</h3>
          {hasVariants && <p className="text-white/40 text-[10px] uppercase tracking-[0.2em] mt-1">{product.variant_label || 'Opciones'} · {product.variants.map(v => v.label).join(' / ')}</p>}
        </div>
        <p className={`text-[#D4AF37] font-black whitespace-nowrap ${featured ? 'text-2xl' : 'text-base'}`} style={DISPLAY}>${Number(product.price).toLocaleString('es-AR')}</p>
      </div>

      {/* glass quick-add overlay (desktop hover) */}
      {!soldOut && (
        <div className="absolute inset-0 hidden md:flex flex-col items-center justify-center gap-4 bg-black/60 backdrop-blur-[6px] opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity duration-300"
          data-testid={`merch-quickadd-${product.id}`}>
          {hasVariants && (
            <div className="flex flex-wrap justify-center gap-2 px-6">
              {product.variants.map(v => {
                const out = v.stock !== 0 && v.stock <= 0;
                return (
                  <button key={v.label} disabled={out} onClick={(e) => { e.stopPropagation(); setVariant(v.label); }} aria-label={`${product.variant_label || 'Opción'} ${v.label}`}
                    className={`min-w-[42px] h-10 px-3 text-xs font-bold border transition-colors ${variant === v.label ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10' : out ? 'border-white/15 text-white/25 line-through cursor-not-allowed' : 'border-white/40 text-white hover:border-white'}`}
                    data-testid={`quick-variant-${product.id}-${v.label}`}>
                    {v.label}
                  </button>
                );
              })}
            </div>
          )}
          <button onClick={quickAdd} className="h-11 px-8 bg-[#D4AF37] text-black text-[11px] font-black uppercase tracking-[0.25em] hover:bg-[#F2D272] transition-colors" data-testid={`quick-add-${product.id}`}>
            Agregar al carrito
          </button>
          <button onClick={() => onOpen(product)} className="text-white/60 hover:text-white text-[10px] uppercase tracking-[0.25em] font-bold flex items-center gap-1 transition-colors" data-testid={`view-detail-${product.id}`}>
            Ver detalle <ArrowUpRight size={11} />
          </button>
        </div>
      )}
    </motion.article>
  );
};

/* ---------- bento span pattern ---------- */
const spanFor = (i) => {
  const cycle = i % 7;
  if (cycle === 0) return { cls: 'col-span-12 md:col-span-8 aspect-[4/3] md:aspect-auto md:min-h-[560px]', featured: true };
  if (cycle === 1 || cycle === 2) return { cls: 'col-span-6 md:col-span-4 aspect-[3/4] md:aspect-auto md:min-h-[272px]', featured: false };
  if (cycle === 3 || cycle === 4) return { cls: 'col-span-6 aspect-[3/4] md:aspect-[4/3]', featured: false };
  return { cls: 'col-span-6 md:col-span-4 aspect-[3/4]', featured: false };
};

export default function MerchStorePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const eventFilter = searchParams.get('evento');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const { openCart, count } = useMerchCart();

  useEffect(() => {
    axios.get(`${API}/api/merch/products`).then(r => setProducts(r.data.products || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const eventTabs = useMemo(() => {
    const map = new Map();
    products.forEach(p => { if (p.event) map.set(p.event.id, p.event.title); });
    return [...map.entries()];
  }, [products]);

  const visible = useMemo(() => eventFilter ? products.filter(p => p.event_id === eventFilter) : products, [products, eventFilter]);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />

      <div className="pt-24 md:pt-28" data-testid="merch-store-page">
        {/* ------- editorial hero ------- */}
        <div className="px-6 lg:px-12 max-w-[1400px] mx-auto pt-10 md:pt-16 pb-8 md:pb-12">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[#D4AF37] text-[10px] md:text-xs uppercase tracking-[0.5em] font-bold mb-4">Bravo Producciones · Colección oficial</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="text-white uppercase font-black leading-[0.95] text-5xl sm:text-6xl lg:text-[7rem]" style={DISPLAY}>
            Merch<span className="text-[#D4AF37]">.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }} className="text-white/40 text-sm md:text-base max-w-md mt-5 leading-relaxed">
            Piezas exclusivas de la escena. Ediciones limitadas de cada evento — cuando se agotan, no vuelven.
          </motion.p>
        </div>

        {/* ------- gold marquee ribbon ------- */}
        <div className="relative overflow-hidden bg-[#D4AF37] py-2.5 -rotate-[0.6deg] scale-[1.01]" data-testid="merch-marquee">
          <style>{`@keyframes merchMarquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
          <div className="flex whitespace-nowrap w-max" style={{ animation: 'merchMarquee 26s linear infinite' }}>
            {[0, 1].map(k => (
              <span key={k} className="text-black text-xs md:text-sm font-black uppercase tracking-[0.35em]" style={DISPLAY}>
                {Array(6).fill('Bravo exclusive drops · Merch oficial · Ediciones limitadas · ').join('')}
              </span>
            ))}
          </div>
        </div>

        {/* ------- event filter pills ------- */}
        {eventTabs.length > 0 && (
          <div className="sticky top-16 md:top-20 z-30 bg-[#0A0A0A]/85 backdrop-blur-md border-b border-white/5">
            <div className="px-6 lg:px-12 max-w-[1400px] mx-auto flex gap-2 overflow-x-auto py-3 scrollbar-hide" data-testid="merch-event-filters">
              <button onClick={() => setSearchParams({})}
                className={`shrink-0 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.25em] border transition-colors ${!eventFilter ? 'border-[#D4AF37] text-[#D4AF37]' : 'border-white/15 text-white/50 hover:text-white'}`}
                data-testid="merch-filter-all">
                Todos los drops
              </button>
              {eventTabs.map(([id, title]) => (
                <button key={id} onClick={() => setSearchParams({ evento: id })}
                  className={`shrink-0 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.25em] border transition-colors ${eventFilter === id ? 'border-[#D4AF37] text-[#D4AF37]' : 'border-white/15 text-white/50 hover:text-white'}`}
                  data-testid={`merch-filter-${id}`}>
                  {title}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ------- bento grid ------- */}
        <div className="px-6 lg:px-12 max-w-[1400px] mx-auto py-10 md:py-16 pb-24">
          {loading ? (
            <p className="text-white/30 text-xs uppercase tracking-[0.3em]">Cargando drops…</p>
          ) : visible.length === 0 ? (
            <div className="py-28 md:py-40 text-center" data-testid="merch-empty-state">
              <p className="text-white/10 uppercase font-black leading-none text-5xl sm:text-7xl lg:text-9xl select-none" style={DISPLAY}>No hay<br />merch</p>
              <p className="text-white/40 text-sm mt-8 uppercase tracking-[0.3em]">Los próximos drops se anuncian acá</p>
              {eventFilter && <button onClick={() => setSearchParams({})} className="mt-6 text-[#D4AF37] text-xs uppercase tracking-[0.25em] font-bold underline underline-offset-4" data-testid="merch-empty-clear-filter">Ver todos los drops</button>}
            </div>
          ) : (
            <div className="grid grid-cols-12 gap-3 md:gap-5 auto-rows-auto" data-testid="merch-bento-grid">
              {visible.map((p, i) => (
                <ProductTile key={p.id} product={p} index={i} span={spanFor(i)} onOpen={setSelected} />
              ))}
            </div>
          )}
        </div>
      </div>

      {count > 0 && (
        <button onClick={openCart} aria-label="Ver carrito" className="fixed bottom-20 md:bottom-8 right-6 z-[70] bg-[#D4AF37] text-black px-6 py-4 font-black text-[11px] uppercase tracking-[0.25em] shadow-[0_8px_40px_rgba(212,175,55,0.45)] flex items-center gap-2.5 hover:bg-[#F2D272] transition-colors" style={DISPLAY} data-testid="floating-cart-btn">
          <ShoppingBag size={15} /> Carrito ({count})
        </button>
      )}

      {selected && <ProductModal product={selected} onClose={() => setSelected(null)} />}
      <Footer />
    </div>
  );
}
